import React, { useRef } from 'react';
import { useMutation } from '@apollo/client';
import ScanBarcodeMutation from './ScanBarcodeMutation';

interface IScanBarcodeConnectorApolloProps {
  appBrand: string;
  children: ({
    scanBarcode,
    cancelScanBarcode,
  }: {
    scanBarcode: (ean: string) => Promise<unknown>;
    cancelScanBarcode: () => void;
  }) => React.ReactElement;
}

const ScanBarcodeConnectorApollo: React.FC<IScanBarcodeConnectorApolloProps> = ({
  children,
  appBrand,
}) => {
  const [scanBarcodeMutation] = useMutation(ScanBarcodeMutation);
  const abortControllerRef = useRef<AbortController | null>(null);

  const scanBarcode = async (ean: string) => {
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    const response = await scanBarcodeMutation({
      variables: { ean },
      context: {
        fetchOptions: {
          signal: abortControllerRef.current.signal,
        },
      },
    });

    if (
      response.data?.dailyTracker?.fetchProductByEan.__typename ===
      'AnalyzedDailyTrackerIngredients'
    ) {
      // modify response
      let appBrandWithApendix = '';
      if (appBrand === 'dtba') {
        appBrandWithApendix = 'dl-space.fra1';
      } else if (appBrand === 'hashimoto' || appBrand === 'oxy') {
        appBrandWithApendix = `${appBrand}.fra1`;
      } else if (appBrand === 'maxx') {
        appBrandWithApendix = `maxxnation.fra1`;
      } else {
        appBrandWithApendix = `${appBrand}.sfo2`;
      }

      const modifiedResponse = {
        __typename: response.data?.dailyTracker?.fetchProductByEan.__typename,
        mealName: response.data?.dailyTracker?.fetchProductByEan?.primaryIngredient.name,
        requestId: response.data?.dailyTracker?.fetchProductByEan?.requestId,
        portions: 1,
        ingredients: [
          {
            ...response.data?.dailyTracker?.fetchProductByEan?.primaryIngredient,
          },
        ],
        imageUrl: `https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/tracker/tracker_meal.placeholder.jpg`,
      };
      return modifiedResponse;
    } else {
      return response.data?.dailyTracker?.fetchProductByEan;
    }
  };

  const cancelScanBarcode = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  return children({ scanBarcode, cancelScanBarcode });
};

export default ScanBarcodeConnectorApollo;
